import { combineEpics, ActionsObservable, StateObservable, ofType } from 'redux-observable';
import { RootAction, RootState } from '@src/app/_redux';
import { ApiClient } from '@src/app/_global/utility/apiUtils';
import { ApiConfig } from '@src/app/_global/domain';
import { map, mergeMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import { concat } from 'rxjs/observable/concat';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { actionCreators, ORDERFORECAST_START, PROMOCODEINQ_START, RENEWALS_START } from '@src/app/accounts/renewals/actions';
import { actionCreators as weakActionCreators } from '@src/app/login/weak/actions';
import { actionCreators as gaActionCreators } from '@src/app/analytics/googleAnalytics/actions';
import { getLoggedInCustomer } from '@src/app/accounts/selectors';
import { OrderForecastPostBody, RenewalPostBody } from '@src/_api/payload/renewals';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { PromotionCodeInqBody } from '@src/_api/payload/promotions';

const RENEWALS_PATH = '/renewal';
const PROMOCODEINQ_PATH = '/promocodeinquiry';
const getAllSucceeded = (...responses) => {
  const successResponses = responses.filter(item => 
    item.success === true);
  return successResponses;
};
const getAllPromoCodeInqSucceeded = (...responses) => {
  const successResponses = responses.filter(item => item.success);
  return successResponses.map(resp => {
    return resp.promotionCode;
  });
};
const getAllOrderForecastSucceeded = (...responses) => {
  const successResponses = responses.filter(item => item.success);
  return successResponses.map(resp => {
    return resp;
  });
};
const startRenewal =  (
  action$: ActionsObservable<RootAction>,
  state$: StateObservable<RootState>,
) =>
    action$.pipe(
      ofType(RENEWALS_START),
      mergeMap(({ payload }) => {
      const client = new ApiClient();
      const clientConfig = state$.value.clientConfig.clientConfig;
      const loading = of(actionCreators.renewalsLoading(true));

      const promises = payload.renewalPayloads.map(renewal => {
        let putBody: RenewalPostBody = renewal;

        const apiConfig: ApiConfig = {
          credentialsObject: state$.value.auth.omsAuth.credentials,
          providerType: clientConfig.providerType,
          url: clientConfig.url,
          path: RENEWALS_PATH,
          region: clientConfig.Region,
          requestmethod: 'POST',
          requestbody: putBody,
          parms: {},
          additionalParms: {
            headers: {
              'x-session-id': clientConfig.uuid
            },
          },
        };
        return fromPromise(client.callApiGateway(apiConfig));
      });
      let loggedIncustomer = getLoggedInCustomer(state$.value);
      const refetchCustomerPayload = {
        accountNumber: loggedIncustomer.accountNumber,
        postalCode: loggedIncustomer.postalCode,
      };
      let reloadCust = of(weakActionCreators.refetchCustomer(refetchCustomerPayload));
      const source$ = forkJoin(promises, getAllSucceeded).pipe(
        map(responses => {
          let payloadCount = payload.renewalPayloads.length;
          let gaCategory = payload.renewalPayloads[0].googleAnalyticsCategory;
          if (payloadCount === responses.length) {
            responses.forEach(renewalResponse => {
              let matchingRenewalPayload = payload.renewalPayloads.find((renewalPayload) => 
                renewalPayload.confPageFields.productName === renewalResponse.order.orderItems[0].productName);
              gaActionCreators.createGAEvent({
                category: gaCategory,
                action: 'Successful',
                value:  renewalResponse.order.payment.amountPaid * 100,  // value can only be an integer
                label: matchingRenewalPayload.googleAnalyticsLabel
              });
            });
            return actionCreators.renewalsSuccess({payload, responses, success: true});
          } else {
            responses.forEach(renewalResponse => {
              let matchingRenewalPayload = payload.renewalPayloads.find((renewalPayload) => 
                renewalPayload.confPageFields.productName === renewalResponse.order.orderItems[0].productName);
              gaActionCreators.createGAEvent({
                category: gaCategory,
                action: 'Unsuccessful',
                value:  renewalResponse.order.payment.amountPaid * 100,
                label: matchingRenewalPayload.googleAnalyticsLabel
              });
            });
            return actionCreators.renewalsError({
              success: false,
              message: 'Error on Renewal',
            });
          }
        }),
        catchError(err => {
          gaActionCreators.createGAEvent({
            category:  payload.renewalPayloads[0].googleAnalyticsCategory,
            action: 'Unknown Error',
            label: payload.renewalPayloads[0].googleAnalyticsLabel
          });
          return of(
            actionCreators.renewalsError({ success: false, message: err }),
          );
        }),
      );
      return concat(loading, source$, reloadCust);
    }),
  );
  const promotionCodeInquiry = (
    action$: ActionsObservable<RootAction>,
    state$: StateObservable<RootState>,
  ) =>
    action$.pipe(
      ofType(PROMOCODEINQ_START),
      mergeMap(({ payload }) => {
        const clientConfig = state$.value.clientConfig.clientConfig;
        const loading = of(actionCreators.promoCodeInqLoading(true));
        const client = new ApiClient();
  
        const promises = payload.promoCodeInqOrders.map((item: any) => { 
          let postBody: PromotionCodeInqBody = {
            prodIdAlias: payload.customer.prodIdAlias,
            promotionCode: item.promotionKey,
          };
          const apiConfig: ApiConfig = {
            credentialsObject: state$.value.auth.omsAuth.credentials,
            providerType: clientConfig.providerType,
            url: clientConfig.url,
            path: PROMOCODEINQ_PATH,
            region: clientConfig.Region,
            requestmethod: 'POST',
            requestbody: postBody,
            parms: {},
            additionalParms: {
              headers: {
                'x-session-id': clientConfig.uuid
              },
            },
          };
          return fromPromise(client.callApiGateway(apiConfig));
        });
  
        const source$ = forkJoin(promises, getAllPromoCodeInqSucceeded).pipe(
          map(allSucceeded => {
            if (allSucceeded) {
              return actionCreators.promoCodeInqSuccess({promotionCodes: allSucceeded});
            } else {
              return actionCreators.promoCodeInqError({
                success: false,
                message: 'Error on Promotion Code Inq',
              });
            }
          }),
          catchError(err => {
            gaActionCreators.createGAEvent({
              category: payload.promoCodeInqOrders[0].googleAnalyticsCategory,
              action: 'Unknown Error',
              label: payload.promoCodeInqOrders[0].prodIdAlias
            });
            return of(
              actionCreators.promoCodeInqError({ success: false, message: err }),
            );
          }),
        );
        return concat(loading, source$);
      }),
    );

const startOrderForecast = (
  action$: ActionsObservable<RootAction>,
  state$: StateObservable<RootState>,
) =>
  action$.pipe(
    ofType(ORDERFORECAST_START),
    mergeMap(({ payload }) => {
      const clientConfig = state$.value.clientConfig.clientConfig;
      const loading = of(actionCreators.orderForecastLoading(true));
      const client = new ApiClient();

      const promises = payload.renewalOrders.map((item: any) => {
        let orderItems: any = [];

        let promotionCode = payload.promoCodeInqResult 
          && payload.promoCodeInqResult.promotionCodes.find((promoCode) => 
           item.promotionKey === promoCode.promotionCode);

        let recipAccount = payload.associatedGifts.associatedGiftAccounts[item.shipToAccountNumber];

        if (promotionCode) {
          promotionCode.products.forEach((product, productIndex) => {
            if (product.choices.length > 0) {
              product.choices.forEach((choice) => {
                orderItems.push({
                  itemNumber: choice.itemNumber,
                  quantity: '1',
                  choiceCode: choice.choiceCode,
                  accountNumber: recipAccount ? recipAccount.accountNumber : payload.customer.accountNumber,
                  addressId: recipAccount ? recipAccount.address.addressId : payload.customer.address.addressId,
                });
              });
            }
          });
        }

        let postBody: OrderForecastPostBody = {
          prodIdAlias: payload.customer.prodIdAlias,
          promotionCode: item.promotionKey,
          accountNumber: payload.customer.accountNumber,
          addressId: payload.customer.address.addressId,
          orderItems: orderItems,
          systemCode: 'CORE',
          coreTransactionType: 'orderForecast',
          isRenewalOrderForcast: true,
        };
        const apiConfig: ApiConfig = {
          credentialsObject: state$.value.auth.omsAuth.credentials,
          providerType: clientConfig.providerType,
          url: clientConfig.url,
          path: RENEWALS_PATH,
          region: clientConfig.Region,
          requestmethod: 'POST',
          requestbody: postBody,
          parms: {},
          additionalParms: {
            headers: {
              'x-session-id': clientConfig.uuid
            },
          },
        };
        return fromPromise(client.callApiGateway(apiConfig));
      });

      const source$ = forkJoin(promises, getAllOrderForecastSucceeded).pipe(
        map(allSucceeded => {
          if (allSucceeded) {
            return actionCreators.orderForecastSuccess({orderForecast: allSucceeded});
          } else {
            return actionCreators.orderForecaseError({
              success: false,
              message: 'Error on Order Forecast',
            });
          }
        }),
        catchError(err => {
          gaActionCreators.createGAEvent({
            category: 'Order Forecast',
            action: 'Unknown Error',
            label: payload.renewalOrders[0].prodIdAlias
          });
          return of(
            actionCreators.orderForecaseError({ success: false, message: err }),
          );
        }),
      );
      return concat(loading, source$);
    }),
  );
  
export const epics = combineEpics(startRenewal, promotionCodeInquiry, startOrderForecast);
